// src/views/InstitutionAdminDashboard.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { Spin, Select, Button } from "antd";
import toast from "react-hot-toast";
import Chart from "react-apexcharts";
import PageHeading from "../../components/PageElements/PageHeading";
import Card from "../../components/Cards/Card";
import EventsCalendar from "../../components/Calendar/EventsCalendar";
import { getSessions } from "../../services/sessionService";
import { BsBuilding, BsMap, BsPeople } from "react-icons/bs";
import { getInstitutionDashboardData } from "../../services/dashboardService";

const { Option } = Select;

const InstitutionAdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [applying, setApplying] = useState(false);

  const fmt = useCallback(
    (n) => (typeof n === "number" ? n.toLocaleString() : "0"),
    []
  );

  useEffect(() => {
    loadSessions();
    loadDashboardData();
  }, []);

  const loadSessions = async () => {
    try {
      const { data } = await getSessions();
      setSessions(Array.isArray(data?.data) ? data.data : []);
    } catch {
      /* ignore */
    }
  };

  const loadDashboardData = async (sessionId = null) => {
    setLoading(true);
    try {
      const { data } = await getInstitutionDashboardData(sessionId);
      setDashboardData(data || {});
    } catch {
      toast.error("Failed to load institution dashboard", { id: "inst-dash" });
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = async () => {
    setApplying(true);
    try {
      await loadDashboardData(selectedSession);
      toast.success("Filter applied", { id: "inst-filter" });
    } finally {
      setApplying(false);
    }
  };

  const clearFilter = async () => {
    setSelectedSession(null);
    setApplying(true);
    try {
      await loadDashboardData(null);
      toast("Filter cleared", { id: "inst-filter-clear" });
    } finally {
      setApplying(false);
    }
  };

  // KPI Cards
  const cardsItems = [
    {
      id: 1,
      label: "Faculties",
      icon: <BsBuilding size={28} />,
      number: dashboardData?.faculties_count ?? 0,
      bgClass: "bg-indigo-600/80",
      textClass: "text-white",
    },
    {
      id: 2,
      label: "Campuses",
      icon: <BsMap size={28} />,
      number: dashboardData?.campuses_count ?? 0,
      bgClass: "bg-blue-600/80",
      textClass: "text-white",
    },
    {
      id: 3,
      label: "Notable Staff",
      icon: <BsPeople size={28} />,
      number: dashboardData?.notable_staff_count ?? 0,
      bgClass: "bg-yellow-500/80",
      textClass: "text-white",
    },
    {
      id: 4,
      label: "Specialized Facilities",
      icon: <BsBuilding size={28} />,
      number: dashboardData?.specialized_facilities_count ?? 0,
      bgClass: "bg-green-600/80",
      textClass: "text-white",
    },
  ];

  // Gender chart
  const genderChartSeries = useMemo(
    () => [
      {
        name: "Male",
        data: [Number(dashboardData?.gender_distribution?.total_males ?? 0)],
      },
      {
        name: "Female",
        data: [Number(dashboardData?.gender_distribution?.total_females ?? 0)],
      },
    ],
    [dashboardData?.gender_distribution]
  );

  const genderChartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        animations: { easing: "easeinout", speed: 400 },
      },
      plotOptions: { bar: { horizontal: true, borderRadius: 6 } },
      xaxis: {
        categories: ["Gender Distribution"],
        labels: { style: { fontSize: "12px" } },
      },
      yaxis: { title: { text: "Number of Students" } },
      colors: ["#60a5fa", "#fbbf24"],
      legend: { position: "bottom", labels: { colors: "#4b5563" } },
      grid: { borderColor: "#f1f5f9" },
      tooltip: { y: { formatter: (val) => `${fmt(val)} students` } },
    }),
    [fmt]
  );

  // Student growth by session (bar)
  const sessionGrowth = dashboardData?.student_growth_by_session ?? [];
  const sessionLabels = sessionGrowth.map((item) => item.session);
  const sessionValues = sessionGrowth.map((item) =>
    Number(item?.total_students ?? 0)
  );

  const sessionGrowthBarOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        animations: { easing: "easeinout", speed: 400 },
      },
      xaxis: {
        categories: sessionLabels,
        title: { text: "Session Year" },
        labels: { rotate: -45, style: { fontSize: "12px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { title: { text: "Total Students" }, labels: { formatter: fmt } },
      plotOptions: { bar: { borderRadius: 4 } },
      grid: { borderColor: "#f1f5f9" },
      colors: ["#34d399"],
      tooltip: { y: { formatter: (val) => `${fmt(val)} students` } },
    }),
    [sessionLabels, fmt]
  );

  const sessionGrowthBarSeries = useMemo(
    () => [{ name: "Students", data: sessionValues }],
    [sessionValues]
  );

  // ---- Staff by focus area (three donuts: PhD, Professors, Masters) ----
  const staffPhD = dashboardData?.staff_by_focus_area_phd ?? [];
  const staffProf = dashboardData?.staff_by_focus_area_professors ?? [];
  const staffMasters = dashboardData?.staff_by_focus_area_masters ?? [];

  const makeDonut = useCallback(
    (rows) => {
      const series = rows.map((r) => Number(r?.total ?? 0));
      const options = {
        chart: { type: "donut", toolbar: { show: false } },
        labels: rows.map((r) => r?.focus_area ?? "Unknown"),
        legend: { position: "bottom", fontSize: "12px" },
        tooltip: { y: { formatter: (val) => `${fmt(val)} staff` } },
        dataLabels: {
          formatter: (_percent, opts) => {
            const raw = opts.w.globals.series[opts.seriesIndex];
            return `${fmt(raw)}`;
          },
        },
        stroke: { width: 2, colors: ["#fff"] },
        responsive: [
          { breakpoint: 768, options: { legend: { position: "bottom" } } },
        ],
        colors: [
          "#60a5fa",
          "#fbbf24",
          "#34d399",
          "#f87171",
          "#a78bfa",
          "#22d3ee",
          "#fb7185",
          "#84cc16",
        ],
      };
      return { series, options };
    },
    [fmt]
  );

  const phdDonut = useMemo(() => makeDonut(staffPhD), [staffPhD, makeDonut]);
  const profDonut = useMemo(() => makeDonut(staffProf), [staffProf, makeDonut]);
  const mastersDonut = useMemo(
    () => makeDonut(staffMasters),
    [staffMasters, makeDonut]
  );

  const upcomingEvents = dashboardData?.upcoming_events ?? [];

  return (
    <>
      <PageHeading subtitle1="Institution Dashboard" />

      <Spin spinning={loading} tip="Loading dashboard...">
        <div className="px-6 md:px-8 py-6 md:py-8 flex flex-col w-full gap-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {cardsItems.map((item) => (
              <Card item={item} key={item.id} />
            ))}
          </div>

          {/* Events */}
          <div className="grid grid-cols-1 gap-6">
            <div className="rounded-xl bg-white border border-black/5 shadow-sm w-full px-5 py-5">
              <h2 className="font-semibold text-gray-800 mb-4">
                Upcoming Events
              </h2>
              <EventsCalendar eventsData={upcomingEvents} />
              {upcomingEvents.length === 0 && (
                <p className="mt-3 text-sm text-gray-500">
                  No upcoming events.
                </p>
              )}
            </div>
          </div>

          {/* Student growth */}
          <div className="grid grid-cols-1 gap-6">
            <div className="rounded-xl bg-white border border-black/5 shadow-sm w-full px-5 py-5">
              <h2 className="font-semibold text-gray-800 mb-2">
                Student Growth by Academic Year
              </h2>
              {sessionLabels.length > 0 ? (
                <Chart
                  options={sessionGrowthBarOptions}
                  series={sessionGrowthBarSeries}
                  type="bar"
                  height={320}
                />
              ) : (
                <p className="text-sm text-gray-500">
                  No session data available.
                </p>
              )}
            </div>
          </div>

          {/* Filter */}
          <div className="rounded-xl bg-white border border-black/5 shadow-sm w-full px-5 py-5">
            <label className="block font-medium text-gray-800 mb-3">
              Filter By Year
            </label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <Select
                placeholder="Select year"
                size="middle"
                value={selectedSession}
                onChange={(value) => setSelectedSession(value)}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: 260 }}
              >
                {sessions.map((session) => (
                  <Option key={session.id} value={session.id}>
                    {session.name}
                  </Option>
                ))}
              </Select>

              <div className="flex gap-2">
                <Button
                  type="primary"
                  size="middle"
                  loading={applying}
                  className="bg-primary! disabled:text-white! disabled:bg-primary/40! disabled:cursor-not-allowed"
                  onClick={applyFilter}
                  disabled={!selectedSession}
                >
                  Apply
                </Button>
                <Button
                  onClick={clearFilter}
                  disabled={applying || selectedSession === null}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>

          {/* Gender + Staff donuts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Gender */}
            <div className="rounded-xl bg-white border border-black/5 shadow-sm w-full px-5 py-5">
              <h2 className="font-semibold text-gray-800 mb-2">
                Student Gender Distribution
              </h2>
              {genderChartSeries.some((s) =>
                Array.isArray(s.data) ? s.data[0] > 0 : false
              ) ? (
                <Chart
                  options={genderChartOptions}
                  series={genderChartSeries}
                  type="bar"
                  height={300}
                  width="100%"
                />
              ) : (
                <p className="text-sm text-gray-500">
                  No gender data available.
                </p>
              )}
            </div>

            {/* PhD donut */}
            <div className="rounded-xl bg-white border border-black/5 shadow-sm w-full px-5 py-5">
              <h2 className="font-semibold text-gray-800 mb-2">
                PhD Staff by Focus Area
              </h2>
              {phdDonut.series.length > 0 &&
              phdDonut.series.some((v) => v > 0) ? (
                <Chart
                  options={phdDonut.options}
                  series={phdDonut.series}
                  type="donut"
                  height={320}
                />
              ) : (
                <p className="text-sm text-gray-500">
                  No staff data available.
                </p>
              )}
            </div>
          </div>

          {/* Professors + Masters donuts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Professors */}
            <div className="rounded-xl bg-white border border-black/5 shadow-sm w-full px-5 py-5">
              <h2 className="font-semibold text-gray-800 mb-2">
                Professor Staff by Focus Area
              </h2>
              {profDonut.series.length > 0 &&
              profDonut.series.some((v) => v > 0) ? (
                <Chart
                  options={profDonut.options}
                  series={profDonut.series}
                  type="donut"
                  height={320}
                />
              ) : (
                <p className="text-sm text-gray-500">
                  No staff data available.
                </p>
              )}
            </div>

            {/* Masters */}
            <div className="rounded-xl bg-white border border-black/5 shadow-sm w-full px-5 py-5">
              <h2 className="font-semibold text-gray-800 mb-2">
                Masters Staff by Focus Area
              </h2>
              {mastersDonut.series.length > 0 &&
              mastersDonut.series.some((v) => v > 0) ? (
                <Chart
                  options={mastersDonut.options}
                  series={mastersDonut.series}
                  type="donut"
                  height={320}
                />
              ) : (
                <p className="text-sm text-gray-500">
                  No staff data available.
                </p>
              )}
            </div>
          </div>
        </div>
      </Spin>
    </>
  );
};

export default InstitutionAdminDashboard;
